import React from "react";

const MAX_OPTIONS = 3;
const MAX_VARIANTS = 100;

function comboKey(options) {
  return Object.keys(options || {})
    .sort()
    .map((name) => `${name}=${options[name]}`)
    .join("|");
}

function combinations(options) {
  let rows = [[]];
  options.forEach((option) => {
    const next = [];
    rows.forEach((row) => {
      option.values.forEach((value) => {
        next.push({ ...row, [option.name]: value });
      });
    });
    rows = next;
  });
  return rows.length === 1 && Object.keys(rows[0] || {}).length === 0 ? [] : rows;
}

export function catalogFromDraft(draft) {
  const options = [];
  const names = {};

  (draft?.options || []).forEach((option) => {
    if (options.length >= MAX_OPTIONS) return;
    const name = String(option.name || "").trim();
    const values = [];
    const seen = {};
    String(option.valuesText || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((value) => {
        const token = value.toLowerCase();
        if (seen[token]) return;
        seen[token] = true;
        values.push(value);
      });

    if (!name && values.length === 0) return;
    if (!name) {
      options.push({ error: "Type a name for this choice, such as Size or Color." });
      return;
    }
    if (names[name.toLowerCase()]) {
      options.push({ error: "Use a different name for each choice. Size and Color can both exist. Two choices cannot both be named Size." });
      return;
    }
    if (values.length === 0) {
      options.push({ error: `Type the choices for ${name}, separated by commas. Example: S, M, L` });
      return;
    }
    names[name.toLowerCase()] = true;
    options.push({ name, values });
  });

  const broken = options.find((option) => option.error);
  if (broken) return { error: broken.error, options: draft?.options || [], items: draft?.items || [] };

  const combos = combinations(options);
  if (combos.length > MAX_VARIANTS) {
    return {
      error: `That makes more than ${MAX_VARIANTS} combinations. Remove a size, color, or other choice. 10 sizes and 10 colors is the maximum.`,
      options: draft?.options || [],
      items: draft?.items || [],
    };
  }

  const byKey = {};
  (draft?.items || []).forEach((item) => {
    byKey[comboKey(item.options || {})] = item;
  });

  const items = combos.map((combo) => {
    const existing = byKey[comboKey(combo)] || {};
    return {
      title: Object.values(combo).join(" / "),
      options: combo,
      price: existing.price ?? "",
      compare_price: existing.compare_price ?? "",
      cost_price: existing.cost_price ?? "",
      sku: existing.sku ?? "",
      qty: existing.qty ?? 0,
    };
  });

  for (let index = 0; index < items.length; index += 1) {
    const price = Number(items[index].price);
    const cost = Number(items[index].cost_price || 0);
    const qty = Number(items[index].qty);
    if (!(price > 0)) {
      return { error: "Type the price the customer pays on every row.", options, items };
    }
    if (cost > 0 && price <= cost) {
      return { error: "Each selling price must be higher than that row's buying price.", options, items };
    }
    if (Number.isNaN(qty) || qty < 0) {
      return { error: "Stock cannot be less than 0.", options, items };
    }
  }

  const selling = items.length ? Math.min(...items.map((item) => Number(item.price))) : 0;
  const qty = items.reduce((sum, item) => sum + Number(item.qty || 0), 0);

  return { options, items, selling, qty, error: "" };
}

export function draftFromCatalog(catalog) {
  const options = (catalog?.options || []).map((option) => ({
    name: option.name || "",
    valuesText: (option.values || []).join(", "),
  }));
  const items = (catalog?.variants || []).map((variant) => ({
    title: variant.title,
    options: variant.options || {},
    price: variant.price ?? "",
    compare_price: variant.compare_price || "",
    cost_price: variant.cost_price ? variant.cost_price : "",
    sku: variant.sku || "",
    qty: variant.qty ?? 0,
  }));
  return { options, items, error: "" };
}

export default function ProductVariantsEditor({ value, onChange }) {
  const options = value?.options || [];
  const items = value?.items || [];

  const emitOptions = (nextOptions) => {
    const built = catalogFromDraft({ options: nextOptions, items });
    onChange({
      options: nextOptions,
      items: built.items || items,
      error: built.error || "",
    });
  };

  const updateOption = (index, field, fieldValue) => {
    emitOptions(options.map((option, optionIndex) => (
      optionIndex === index ? { ...option, [field]: fieldValue } : option
    )));
  };

  const removeOption = (index) => {
    emitOptions(options.filter((_, optionIndex) => optionIndex !== index));
  };

  const addOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    emitOptions([...options, { name: "", valuesText: "" }]);
  };

  const updateItem = (index, field, fieldValue) => {
    onChange({
      ...value,
      items: items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: fieldValue } : item
      )),
      error: "",
    });
  };

  return (
    <div>
      <div className="small text-muted mb-3">
        <p className="mb-2">
          Use this when one product comes in different sizes, colors, or materials. Selling price, buying price, and stock then belong on each row. Skip this when every customer pays the same price.
        </p>
        <ol className="mb-2 ps-3">
          <li>Click <strong>Add a choice</strong>. Name it Size, Color, or Material.</li>
          <li>Type the choices separated by commas, such as <strong>S, M, L</strong>.</li>
          <li>Add a second choice if you need one. Size and Color together make a row for every pair, such as S / Black. Three choices is the maximum.</li>
          <li>On each row, type the selling price, what you paid, and how many you have. Old price and item code can stay empty.</li>
        </ol>
        <p className="mb-0">
          You can have up to 100 rows. Ten sizes and ten colors is 100. Remove a choice if the list gets longer than that.
        </p>
      </div>

      {options.map((option, index) => (
        <div className="row g-2 align-items-end mb-2" key={`option-${index}`}>
          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold small mb-1">What is the customer choosing?</label>
            <input
              className="form-control"
              placeholder="Size"
              aria-label="Choice name, such as Size or Color"
              value={option.name}
              onChange={(event) => updateOption(index, "name", event.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold small mb-1">List the choices, separated by commas</label>
            <input
              className="form-control"
              placeholder="S, M, L"
              aria-label="Choices separated by commas, such as S, M, L"
              value={option.valuesText}
              onChange={(event) => updateOption(index, "valuesText", event.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeOption(index)}>
              Remove
            </button>
          </div>
        </div>
      ))}

      {options.length < MAX_OPTIONS && (
        <button type="button" className="btn btn-outline-dark btn-sm mb-3" onClick={addOption}>
          Add a choice
        </button>
      )}

      {value?.error && (
        <div className="alert alert-warning py-2 small">{value.error}</div>
      )}

      {items.length > 0 && (
        <>
        <p className="small text-muted mb-2">
          Each row is one thing a customer can buy. <strong>Price</strong> is what they pay. <strong>Buying price</strong> is what you paid for that choice. <strong>Old price</strong> is optional and shows crossed out when it is higher. <strong>Item code</strong> can stay empty. <strong>In stock</strong> is how many of that exact choice you have.
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Customer picks</th>
                <th>Price</th>
                <th>Buying price</th>
                <th>Old price</th>
                <th>Item code</th>
                <th>In stock</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={comboKey(item.options) || index}>
                  <td className="fw-semibold">{item.title}</td>
                  <td style={{ minWidth: "110px" }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="form-control form-control-sm"
                      aria-label={`${item.title} price`}
                      value={item.price}
                      onChange={(event) => updateItem(index, "price", event.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: "110px" }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="form-control form-control-sm"
                      aria-label={`${item.title} buying price`}
                      value={item.cost_price}
                      onChange={(event) => updateItem(index, "cost_price", event.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: "110px" }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="form-control form-control-sm"
                      aria-label={`${item.title} compare price`}
                      value={item.compare_price}
                      onChange={(event) => updateItem(index, "compare_price", event.target.value)}
                    />
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    <input
                      className="form-control form-control-sm"
                      aria-label={`${item.title} sku`}
                      value={item.sku}
                      onChange={(event) => updateItem(index, "sku", event.target.value)}
                    />
                  </td>
                  <td style={{ width: "90px" }}>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      aria-label={`${item.title} stock`}
                      value={item.qty}
                      onChange={(event) => updateItem(index, "qty", event.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
